import { attr, css, customElement } from '@genesislcap/web-core';
import { GridOptions } from '@ag-grid-community/core';
import { getGridProTemplate, RapidGridPro, rapidGridProStyles, themeGenesisRapidDark } from '@genesislcap/rapid-grid-pro';

@customElement({
  name: 'mbm-grid-pro',
  template: getGridProTemplate('rapid'),
      styles: css`
      ${rapidGridProStyles}

      .ag-theme-genesis-rapid-dark {
        --ag-background-color: #181d1f;
        --ag-row-background-color: #181d1f;
        --ag-selected-row-background-color: #1f425f;
      }

      /* Hover: same as selected (accent at 0.3 opacity) */
      .ag-theme-genesis-rapid-dark .ag-row.ag-row-hover,
      .ag-theme-genesis-rapid-dark .ag-row-hover .ag-cell {
        background-color: color-mix(in srgb, #1f425f 30%, transparent) !important;
      }
        
      .ag-theme-genesis-rapid-dark .ag-row,
      .ag-theme-genesis-rapid-dark .ag-body-viewport,
      .ag-theme-genesis-rapid-dark .ag-center-cols-viewport,
      .ag-theme-genesis-rapid-dark .ag-body-horizontal-scroll-viewport {
        background-color: #181d1f;
      }

      .ag-theme-genesis-rapid-dark .ag-cell {
        color: #c9c9c9;
        font-size: var(--type-ramp-minus-2-font-size);
      }
      
      .bid-price-cell {
        background-color: var(--neutral-layer-4);
      }
      
      .bid-price-cell:hover {
        border: 1px solid white;
      }
      
      /* Override bid-price-cell background when row is selected */
      .ag-row-selected .bid-price-cell,
      .ag-row.ag-row-selected .bid-price-cell,
      .ag-theme-genesis-rapid-dark .ag-row-selected .bid-price-cell,
      .ag-theme-genesis-rapid-light .ag-row-selected .bid-price-cell {
        background-color: transparent !important;
      }
      
      /* Override bid-price-cell background when row is hovered */
      .ag-row-hover .bid-price-cell,
      .ag-theme-genesis-rapid-dark .ag-row-hover .bid-price-cell,
      .ag-theme-genesis-rapid-light .ag-row-hover .bid-price-cell {
        background-color: transparent !important;
      }
      


      .ag-cell-editor {
        border: 1px solid white;
      }
      
      .ag-cell-inline-editing {
        border-color: transparent !important;
        border-radius: 0;
      }
        
      .ag-picker-wrapper {
        border: none;
        border-radius: 0;
        background: none;
        box-shadow: none;
      }
      
      .ag-picker-wrapper {
        border: none;
        border-color: transparent;
        border-radius: 0;
        background: none;
        box-shadow: none;
      }
    `,
})
export class MbmGridPro extends RapidGridPro {

  @attr theme: string = themeGenesisRapidDark;

  set gridOptions(options: GridOptions ) {
    super.gridOptions = {
      // Enable range selection for cell selection
      enableRangeSelection: true,
      tooltipShowDelay: 500,
      suppressCopyRowsToClipboard: true,

      // Add custom context menu items
      getContextMenuItems: (params) => {
        const result = [
          {
            name: 'Copy Cell',
            shortcut: 'Ctrl+C',
            icon: '<span class="ag-icon ag-icon-copy"></span>',
            action: () => {
              const cellValue = this.getFormattedCellValue(params.node.data, params.column);
              navigator.clipboard?.writeText(cellValue).catch(err => 
                console.warn('Failed to copy to clipboard:', err)
              );
            }
          },
          {
            name: 'Copy Row',
            icon: '<span class="ag-icon ag-icon-copy"></span>',
            action: () => {
              const rowData = params.node.data;
              const rowText = Object.values(rowData).join(',');
              navigator.clipboard?.writeText(rowText).catch(err => 
                console.warn('Failed to copy to clipboard:', err)
              );
            }
          },

          'copyWithHeaders',
          'paste',
          'separator',
          'export'
        ];
        
        return result;
      },
      
      onCellKeyDown: (params) => {
        const event = params.event as KeyboardEvent;
        
        if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
          event.preventDefault();
          
          const focusedCell = params.api.getFocusedCell();
          if (focusedCell) {
            const node = params.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
            if (node?.data) {
              const cellValue = this.getFormattedCellValue(node.data, focusedCell.column);
              navigator.clipboard?.writeText(cellValue).catch(err => 
                console.warn('Failed to copy to clipboard:', err)
              );
            }
          }
        }
      },
      ...options
    };
  }

  private getFormattedCellValue(data: any, column: any): string {
    const rawValue = data[column.getColId()];
    const valueFormatter = column.getColDef().valueFormatter;
    
    if (typeof valueFormatter === 'function') {
      try {
        const formattedValue = valueFormatter({ value: rawValue, data });
        return formattedValue != null ? String(formattedValue) : String(rawValue || '');
      } catch (err) {
        console.warn('Formatting cell for copying failed: ', err);
      }
    }    
    return String(rawValue || '');
  }
}
